import React from 'react'
import { Button, Spacer, Text, Container, Icon, IconStack, Modal, Form } from 'roadie-ui'
import { VehicleTypeSelect, VehicleColorSelect, VehicleColorSelectBubble } from 'components/form/VehicleSelect'
import { getVehicleDisplayName, hasLicensePlateLookupFailure, preventSpecialCharacters } from 'util/vehicle'
import Panel from 'components/layout/Panel'
import { Link } from 'react-router-dom'
import { ModalOptions } from 'types/global'
import { FeatureFlag } from 'Optimizely'
import { useSelector } from 'react-redux'
import { ProfileVehicle } from 'types/profile'
import { capitalize } from 'util/format'
import { states } from 'types/states'
import { useVehiclesViewModel } from './hooks/useVehiclesViewModel'
import { useLicensePlateInputViewModel } from './hooks/useLicensePlateInputViewModel'
import { useLocalization, LocalizedText } from 'localization'

export function getVehicleCategoryIcon(
  vehicleCategory: string | null = 'car',
  vehicleModel: string | null = 'car',
  vin: string | null
) {
  // typically we only use the category to determine the icon, but
  // need an temporary mappings object to ensure correct icons
  // once BE/intel work fixes categories, this will no longer be needed
  const vehicleMappings = {
    sedan: ['car', 'hatchback'],
    hatchback_suv: ['suv'],
    van: ['minivan', 'van'],
    pickup_truck: ['pickup'],
    box_truck: ['box truck'],
  }

  const modelToCategory = (model: string | null) => {
    const modelLowerCase = model?.toLowerCase() || 'car'
    for (const [category, items] of Object.entries(vehicleMappings)) {
      if (items.includes(modelLowerCase)) {
        return category
      }
    }
  }

  let roadieCategory
  if (vin) {
    roadieCategory = vehicleCategory
  } else {
    roadieCategory = modelToCategory(vehicleModel)
  }

  // Adapted from vehicles.roadie_categories table
  switch (roadieCategory) {
    case 'two_door':
      return <Icon.VehicleCar />
    case 'sedan':
      return <Icon.VehicleCar />
    case 'hatchback_suv':
      return <Icon.VehicleSuv />
    case 'van':
      return <Icon.VehicleVan />
    case 'pickup_truck':
      return <Icon.VehiclePickup />
    case 'box_truck':
      return <Icon.VehicleTruck />
    case 'cargo_van':
      return <Icon.VehicleVan />
    case 'large_box_truck':
      return <Icon.VehicleTruck />
    case 'oversized_box_truck':
      return <Icon.VehicleTruck />
    default:
      return <Icon.VehicleCar />
  }
}

const LicensePlateInfoModal: React.FC<{
  modalOptions?: ModalOptions
  setModalOptions?: React.Dispatch<React.SetStateAction<ModalOptions>>
}> = (props) => {
  const isWebView = useSelector<any, boolean>((state) => state.config.isWebview)

  return (
    <Modal.Basic
      open={props.modalOptions?.modalOpen && props.modalOptions?.modalType === 'license-plate'}
      width={!isWebView ? '500px' : '350px'}
    >
      <Container.Base>
        <Container.Position $position="absolute" $top="1.4rem" $right="1.4rem">
          <Button.Tertiary
            type="button"
            onClick={() => props.setModalOptions?.({ modalOpen: false, modalType: null })}
            data-cy="license-info-modal-close-icon"
          >
            <Icon.Close $width="2rem" $height="2rem" $color="neutral-darker" />
          </Button.Tertiary>
        </Container.Position>
        <Spacer $height="1.4rem" />
        <Text.H4 $fontWeight="700" $color="neutral-black">
          <LocalizedText translationKey="dweb_account_driving_info_vehicles_license_plate_info" />
        </Text.H4>
        <Spacer $height="2.4rem" />
        <Text.Body1>
          <LocalizedText translationKey="dweb_driving_info_license_plate_benefits" />
        </Text.Body1>
        <Spacer $height="1.4rem" />
        <Text.Body1>
          <LocalizedText translationKey="dweb_driving_info_license_plate_letters_numbers" />
        </Text.Body1>
        <Spacer $height="2.4rem" />
        <Button.Secondary
          $borderWidth="0"
          $width="100%"
          type="button"
          onClick={() => props.setModalOptions?.({ modalOpen: false, modalType: null })}
          data-cy="license-info-modal-close-button"
        >
          <LocalizedText translationKey="dweb_common_close" />
        </Button.Secondary>
      </Container.Base>
    </Modal.Basic>
  )
}

const LicensePlateInput: React.FC<{
  defaultValues?: any
  errorState?: any
  setFormDataChanged?: React.Dispatch<React.SetStateAction<boolean>>
}> = (props) => {
  const { t } = useLocalization()
  const { licenseState, licensePlate, modalOptions, setModalOptions, handlePlateChange, handleStateChange } =
    useLicensePlateInputViewModel({
      defaultValues: props.defaultValues,
      setFormDataChanged: props.setFormDataChanged,
    })

  return (
    <>
      <Container.Grid $gridTemplateColumns="1fr .5fr" $gridGap="1rem" $alignItems="baseline">
        <Form.Input
          label={t('dweb_account_driving_info_vehicle_info_license_plate')}
          type="text"
          name="license_plate"
          maxLength={8}
          value={licensePlate}
          onChange={handlePlateChange}
          onKeyDown={preventSpecialCharacters}
          data-cy="license-plate-input"
        />
        <Container.Position $position="relative">
          <Form.Select
            key="state"
            label={t('dweb_account_driving_info_vehicle_info_state')}
            name="license_state"
            value={licenseState}
            onChange={handleStateChange}
            data-cy="license-state-select"
          >
            {states.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </Form.Select>
        </Container.Position>
      </Container.Grid>

      {props.errorState && (
        <Container.Base $textAlign="left">
          <Spacer $height="0.4rem" />
          <Text.Caption $color="error-base">{props.errorState}</Text.Caption>
        </Container.Base>
      )}
      <Spacer $height="0.8rem" />

      <Text.Caption
        $textAlign="left"
        $color="primary-base"
        onClick={() => {
          setModalOptions?.({ modalOpen: true, modalType: 'license-plate' })
        }}
      >
        <Button.Tertiary
          type="button"
          $textTransform="none"
          onClick={() => {
            setModalOptions?.({ modalOpen: true, modalType: 'license-plate' })
          }}
          data-cy="license-plate-info-button"
        >
          <LocalizedText translationKey="dweb_account_driving_info_vehicles_why_do_i_need_add" />
        </Button.Tertiary>
      </Text.Caption>

      <LicensePlateInfoModal modalOptions={modalOptions} setModalOptions={setModalOptions} />
    </>
  )
}

export const NewVehicleForm: React.FC<{
  registration?: boolean
  defaultValues?: any //TODO: define shape of defaultValues
  errorState?: any
  setFormDataChanged?: React.Dispatch<React.SetStateAction<boolean>>
}> = (props) => {
  return (
    <Container.Grid>
      <Spacer $height="1.6rem" />
      <Container.GridItem $justifySelf="center" $width="100%" $maxWidth="40rem">
        <Spacer $height="0.8rem" />
        {/* TODO: Removing this feature flag eventually is the goal */}
        <FeatureFlag feature="drive_license_plate_registration_lookup">
          {(enabled) => {
            return enabled ? (
              <>
                {/* Only show VehicleTypeSelect during registration */}
                {props.registration && <VehicleTypeSelect />}
                <VehicleColorSelect defaultValues={props.defaultValues} />
                <LicensePlateInput
                  defaultValues={props.defaultValues}
                  errorState={props.errorState}
                  setFormDataChanged={props.setFormDataChanged}
                />
              </>
            ) : (
              <>
                <VehicleColorSelectBubble />
                <Spacer $height="2.4rem" />
                {/* Only show VehicleTypeSelect during registration */}
                {props.registration && <VehicleTypeSelect />}
              </>
            )
          }}
        </FeatureFlag>
        <Spacer $height="2.4rem" />
      </Container.GridItem>
    </Container.Grid>
  )
}

const VehicleMissingInfoAlert: React.FC<{ alertText: string }> = (props) => {
  return (
    <FeatureFlag feature="drive_enforce_license_plate_and_vin_requirement">
      {(enabled, variables) => {
        // alert_color: 1 = alert-base, 2 = error-base
        const color = variables?.alert_color == 2 ? 'error-base' : 'alert-base'
        return (
          enabled && (
            <>
              <Spacer $height="0.5rem" />
              <Container.Grid $gridTemplateColumns="3.0rem 1fr" $alignItems="center">
                <Icon.AlertFilled $color={color} $width="2.4rem" $height="2.4rem" />
                <Container.Position $padding="0 0 0 0.5rem">
                  <Text.Caption>{props.alertText}</Text.Caption>
                </Container.Position>
              </Container.Grid>
            </>
          )
        )
      }}
    </FeatureFlag>
  )
}

export type VehicleCardProps = {
  icon: React.ReactElement
  title: string
  active: boolean
  vehicle: ProfileVehicle
  allVehicles?: ProfileVehicle[]
  modalOptions?: ModalOptions
  setModalOptions?: React.Dispatch<React.SetStateAction<ModalOptions>>
  page?: string
  errorLabel?: string
}

export const VehicleCard: React.FC<VehicleCardProps> = (props) => {
  // surface alerts if driver does not have certain info on file
  const licensePlateLookupFailure = hasLicensePlateLookupFailure(props.vehicle)
  const missingLicensePlate = props.vehicle.details.license_plate == null
  const missingVIN = props.vehicle.details.vin == null

  const cardContent = (
    <Container.Grid $padding="2.4rem" $gridTemplateColumns="5rem 1fr max-content" $alignItems="center">
      <Container.Position $position="relative" $width="5rem" $height="5rem">
        <IconStack>
          <Container.Base $borderRadius="100%" $backgroundColor="primary-lighter" $height="100%" />
          <Container.Base $color="primary-dark" $padding=".75rem">
            {props.icon}
          </Container.Base>
        </IconStack>
      </Container.Position>
      <Container.Grid $textAlign="left" $padding="0 1rem 0 3rem">
        {props.active && props.page == 'driving-info' && (
          <>
            <Container.Base
              $backgroundColor="success-dark"
              $borderRadius="0.5rem"
              $width="fit-content"
              $padding="0.2rem 1rem 0.1rem 1rem"
            >
              <Text.Button $color="neutral-white">
                <LocalizedText translationKey="dweb_account_driving_info_vehicles_active" />
              </Text.Button>
            </Container.Base>
            <Spacer $height="0.5rem" />
          </>
        )}
        <Text.H5 $fontWeight="400" $color="neutral-black">
          {props.title}
        </Text.H5>
        {props.page == 'vehicle-info' && props.vehicle.details.vin && (
          <Container.Grid $gridTemplateColumns="max-content max-content" $gridGap="1rem" $padding="0.5rem 0">
            <Container.GridItem
              $padding="0.3rem 1rem 0.3rem 0"
              $borderColor="neutral-dark"
              $borderWidth="0 0.15rem 0 0"
              $borderStyle="solid"
            >
              {props.vehicle.specs.year && <Text.Caption>{props.vehicle.specs.year} </Text.Caption>}
            </Container.GridItem>
            <Container.GridItem $padding="0.3rem 0">
              <Text.Caption>{capitalize(props.vehicle.details.color)}</Text.Caption>
            </Container.GridItem>
          </Container.Grid>
        )}
        {missingLicensePlate && <VehicleMissingInfoAlert alertText="Missing license plate info" />}
        {licensePlateLookupFailure && missingVIN && <VehicleMissingInfoAlert alertText="Vehicle not found" />}
        {props.errorLabel && (
          <>
            <Spacer $height="0.4rem" />
            <Text.Caption $color="error-base">{props.errorLabel}</Text.Caption>
          </>
        )}
      </Container.Grid>

      {props.page == 'driving-info' && (
        <Container.Grid $justifyItems="end">
          <Icon.ChevronRight $color="neutral-base" $width="2.4rem" $height="2.4rem" />
        </Container.Grid>
      )}
      {props.page == 'vehicle-info' && (
        <Button.Tertiary
          type="button"
          onClick={() => {
            props.setModalOptions?.({ modalOpen: true, modalType: 'vehicle-info' })
          }}
        >
          <Container.Grid $justifyItems="">
            <Icon.QuestionMarkCircle $color="neutral-base" $width="2.4rem" $height="2.4rem" />
          </Container.Grid>
        </Button.Tertiary>
      )}
    </Container.Grid>
  )

  if (props.page == 'driving-info') {
    return (
      <Button.Link.Tertiary
        id="vehicle-info-link"
        to={`/account/driving-info/vehicle-info/${props.vehicle.details.id}`}
        $display="block"
        $width="100%"
        $textAlign="inherit"
        $textTransform="none"
        $letterSpacing="0"
      >
        {cardContent}
      </Button.Link.Tertiary>
    )
  }

  return cardContent
}

const Vehicles: React.FC = () => {
  const { orderedVehicles, activeVehicleId, isLocked, vehicleLimitReached } = useVehiclesViewModel()

  return (
    <>
      <>
        <Panel left noPadding>
          {orderedVehicles.map((vehicle, index) => {
            const errorLabel =
              vehicle.details.license_plate && !vehicle.details.license_plate_state
                ? 'Missing license plate state'
                : undefined

            return (
              <Panel key={index} left noPadding bottomBorderOnly>
                <VehicleCard
                  icon={getVehicleCategoryIcon(vehicle.specs.category, vehicle.specs.model, vehicle.details.vin)}
                  title={getVehicleDisplayName(vehicle)}
                  active={vehicle.details.id == activeVehicleId}
                  vehicle={vehicle}
                  errorLabel={errorLabel}
                  page="driving-info"
                />
              </Panel>
            )
          })}
          <Link id="new-vehicle-link" to={'/account/driving-info/new-vehicle'}>
            <Button.Tertiary type="button" disabled={vehicleLimitReached || isLocked} $width="100%">
              <Container.Grid $padding="1.6rem" $gridTemplateColumns="5rem 1fr" $alignItems="center">
                <Container.GridItem>
                  <Icon.Plus
                    $color={vehicleLimitReached || isLocked ? 'neutral-base' : 'primary-base'}
                    $width="2.4rem"
                    $height="2.4rem"
                  />
                </Container.GridItem>
                <Container.GridItem $textAlign="left">
                  <LocalizedText translationKey="dweb_account_driving_info_new_vehicle_add_new_vehicle" />
                </Container.GridItem>
              </Container.Grid>
            </Button.Tertiary>
          </Link>
        </Panel>
        {vehicleLimitReached && (
          <>
            <Spacer $height="1rem" />
            <Container.Base $textAlign="center">
              <Text.Caption>
                <LocalizedText translationKey="dweb_account_driving_info_vehicles_max_limit_reached_delete_vehicle_add_more" />
              </Text.Caption>
            </Container.Base>
          </>
        )}
      </>
    </>
  )
}

export default Vehicles
